import React, { useState, useRef, useEffect } from 'react';
import './ShareDropdown.css';
import Modal from './Modal';
import { generateProfilePDFAsBlob } from '../utils/pdfGenerator';

interface ShareDropdownProps {
  profileData: any; // ProfileData
  onDownload: () => void;
}

// Detectar si es dispositivo móvil (excluyendo tablets)
// Tablets se tratan como desktop para mejor UX (copiar enlace es más natural)
const isMobile = (): boolean => {
  // Usar principalmente el ancho de pantalla para detectar
  // Tablets típicamente tienen ancho >= 768px, móviles < 768px
  const isSmallScreen = window.matchMedia && window.matchMedia('(max-width: 767px)').matches;
  
  // Si la pantalla es pequeña, verificar si es realmente un móvil (no tablet)
  if (isSmallScreen) {
    const userAgent = navigator.userAgent;
    // Detectar móviles reales (iPhone, Android phones, etc.)
    // Excluir iPad que puede tener user agent similar a Mac en iOS 13+
    const isPhone = /iPhone|iPod|Android.*Mobile|webOS|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
    return isPhone;
  }
  
  // Pantallas grandes (>= 768px): tratar como desktop/tablet
  return false;
};

const ShareDropdown: React.FC<ShareDropdownProps> = ({ profileData, onDownload }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [isCopying, setIsCopying] = useState(false);
  const [showPdfShareModal, setShowPdfShareModal] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string>('');
  const [urlCopied, setUrlCopied] = useState(false);
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);
  const [linkProgress, setLinkProgress] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSharePDF = async () => {
    try {
      setIsSharing(true);
      setIsOpen(false);
      setIsGeneratingLink(true);
      setLinkProgress(0);
      setUrlCopied(false);
      setPdfUrl(''); // Limpiar URL anterior
      
      // Abrir modal inmediatamente para todos los dispositivos (mostrar "Generando enlace...")
      setShowPdfShareModal(true);
      
      // Generar PDF como blob (guardarlo para usar en mobile después)
      setLinkProgress(20);
      const { blob, fileName } = await generateProfilePDFAsBlob(profileData);
      setLinkProgress(40);
      
      // Obtener path de subida del backend (evita límite de Vercel)
      const apiBaseUrl = process.env.REACT_APP_API_URL 
        ? process.env.REACT_APP_API_URL.replace(/\/api\/?$/, '')
        : '';

      setLinkProgress(50);
      const pathResponse = await fetch(`${apiBaseUrl}/api/get-pdf-upload-path`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      });

      if (!pathResponse.ok) {
        let errorMessage = 'Error al obtener path de subida';
        try {
          const contentType = pathResponse.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const errorData = await pathResponse.json();
            errorMessage = errorData.error || errorData.message || 'Error al obtener path de subida';
          } else {
            const textError = await pathResponse.text();
            if (textError.includes('Request Error') || textError.includes('Error')) {
              errorMessage = 'Error del servidor. Por favor, intenta nuevamente.';
            } else {
              errorMessage = textError.substring(0, 200) || 'Error al obtener path de subida';
            }
          }
        } catch (parseError) {
          errorMessage = `Error ${pathResponse.status}: ${pathResponse.statusText || 'Error al obtener path de subida'}`;
        }
        throw new Error(errorMessage);
      }

      const pathResult = await pathResponse.json();
      if (!pathResult.success || !pathResult.path || !pathResult.fullUrl) {
        throw new Error('No se pudo obtener el path de subida del PDF');
      }

      setLinkProgress(70);
      // Convertir blob a File para subir directamente a Supabase
      const pdfFile = new File([blob], fileName, { type: 'application/pdf' });
      
      // Subir PDF directamente a Supabase Storage (evita límite de Vercel)
      // Usar la misma lógica que otros archivos grandes
      const { createClient } = await import('@supabase/supabase-js');
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
      const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';
      
      if (!supabaseUrl || !supabaseAnonKey) {
        throw new Error(
          'Configuración de Supabase no encontrada. ' +
          'Se requiere REACT_APP_SUPABASE_URL y REACT_APP_SUPABASE_ANON_KEY para subir PDFs.'
        );
      }

      const supabaseClient = createClient(supabaseUrl, supabaseAnonKey);
      
      // IMPORTANTE: Obtener la sesión del usuario para que RLS funcione con 'authenticated'
      // Sin esto, Supabase no sabe que el usuario está autenticado
      const { data: { session }, error: sessionError } = await supabaseClient.auth.getSession();
      
      if (sessionError || !session) {
        // Continuar de todas formas, puede que funcione con las políticas públicas
      } else {
      }
      
      // Obtener nombre del bucket desde la respuesta del backend o usar el default
      const bucketName = pathResult.bucket || process.env.REACT_APP_SUPABASE_BUCKET || 'files';
      
      setLinkProgress(85);
      // Subir directamente a Supabase con upsert para sobrescribir si existe (enlace dinámico)
      const { data: uploadData, error: uploadError } = await supabaseClient.storage
        .from(bucketName)
        .upload(pathResult.path, pdfFile, {
          contentType: 'application/pdf',
          cacheControl: '3600',
          upsert: true // Sobrescribir si existe (enlace dinámico)
        });

      if (uploadError) {
        // Log detallado del error para debugging
        const errorDetails: any = {
          error: uploadError,
          message: uploadError.message,
          bucket: bucketName,
          path: pathResult.path,
          fullError: JSON.stringify(uploadError, null, 2)
        };
        
        // Agregar propiedades opcionales si existen
        if ('statusCode' in uploadError) {
          errorDetails.statusCode = (uploadError as any).statusCode;
        }
        if ('error' in uploadError) {
          errorDetails.errorCode = (uploadError as any).error;
        }
        if ('status' in uploadError) {
          errorDetails.status = (uploadError as any).status;
        }
        
        
        // Detectar errores de RLS o permisos
        const errorMessage = uploadError.message || '';
        const errorCode = (uploadError as any).error || '';
        const statusCode = (uploadError as any).statusCode || (uploadError as any).status;
        
        // Error 400 puede ser RLS o validación
        const isRLSError = 
          errorMessage.includes('row-level security') || 
          errorMessage.includes('RLS') ||
          errorMessage.includes('permission') ||
          errorMessage.includes('Forbidden') ||
          errorMessage.includes('403') ||
          errorMessage.includes('unauthorized') ||
          errorMessage.includes('access denied') ||
          errorCode === 'new row violates row-level security policy' ||
          statusCode === 403 ||
          statusCode === 400; // 400 también puede ser RLS en algunos casos
        
        if (isRLSError) {
          throw new Error(
            `Error de permisos al subir el PDF (${statusCode || 'desconocido'}). ` +
            `Las políticas RLS de Supabase pueden no estar configuradas correctamente. ` +
            `Error: ${errorMessage || errorCode || 'Error desconocido'}. ` +
            `Por favor, verifica que se ejecutó el script de configuración de políticas en Supabase SQL Editor.`
          );
        }
        
        // Si no es error de RLS, mostrar el error específico
        const errorMsg = errorMessage || errorCode || 'Error desconocido al subir PDF';
        throw new Error(`Error al subir PDF a Supabase (${statusCode || 'sin código'}): ${errorMsg}`);
      }

      setLinkProgress(100);
      const generatedPdfUrl = pathResult.fullUrl;
      setPdfUrl(generatedPdfUrl);
      setIsGeneratingLink(false);

      // Mostrar modal con el enlace para todos los dispositivos (mobile y desktop)
      setShowPdfShareModal(true);
      
      // En mobile, también ofrecer compartir el enlace con navigator.share (opcional)
      if (isMobile() && navigator.share) {
        // Intentar compartir el enlace automáticamente en mobile
        // Si el usuario cancela, el modal ya está abierto como alternativa
        try {
          await navigator.share({
            title: 'Perfil Institucional',
            text: 'Comparte este perfil institucional',
            url: generatedPdfUrl
          });
          // Si se compartió exitosamente, cerrar el modal
          setShowPdfShareModal(false);
        } catch (shareError: any) {
          // Si el usuario cancela (AbortError), mantener el modal abierto
          // Si hay otro error, también mantener el modal abierto como alternativa
          if (shareError.name !== 'AbortError') {
          }
          // El modal ya está abierto, el usuario puede copiar el enlace manualmente
        }
      }
    } catch (error: any) {
      // Error al compartir PDF - silenciado
      setIsGeneratingLink(false);
      setLinkProgress(0);
      setShowPdfShareModal(false); // Cerrar modal si hay error
      alert(error.message || 'Error al compartir el PDF. Por favor, intenta descargarlo en su lugar.');
    } finally {
      setIsSharing(false);
    }
  };

  const handleCopyPdfUrl = async () => {
    try {
      await navigator.clipboard.writeText(pdfUrl);
      setUrlCopied(true);
      // Ocultar el mensaje después de 3 segundos
      setTimeout(() => {
        setUrlCopied(false);
      }, 3000);
    } catch (error) {
      alert('Error al copiar la URL. Por favor, cópiala manualmente.');
    }
  };

  return (
    <div className="share-dropdown-container" ref={dropdownRef}>
      <button
        type="button"
        className={`share-dropdown-button ${isOpen ? 'share-dropdown-button-open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Compartir o descargar PDF del perfil"
        disabled={isSharing}
      >
        <svg 
          width="20" 
          height="20" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="currentColor" 
          strokeWidth="2" 
          strokeLinecap="round" 
          strokeLinejoin="round"
        >
          <circle cx="18" cy="5" r="3"></circle>
          <circle cx="6" cy="12" r="3"></circle>
          <circle cx="18" cy="19" r="3"></circle>
          <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
          <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
        </svg>
      </button>
      
      {isOpen && (
        <div className="share-dropdown-menu">
          <button
            type="button"
            className="share-dropdown-item"
            onClick={() => {
              setIsOpen(false);
              onDownload();
            }}
          >
            <svg 
              width="18" 
              height="18" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="7 10 12 15 17 10"></polyline>
              <line x1="12" y1="15" x2="12" y2="3"></line>
            </svg>
            <span>Descargar PDF</span>
          </button>
          
          <button
            type="button"
            className="share-dropdown-item"
            onClick={handleSharePDF}
            disabled={isSharing || isCopying}
          >
            <svg 
              width="18" 
              height="18" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            >
              <circle cx="18" cy="5" r="3"></circle>
              <circle cx="6" cy="12" r="3"></circle>
              <circle cx="18" cy="19" r="3"></circle>
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
              <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
            </svg>
            <span>
              {isSharing || isCopying ? 'Compartiendo...' : 'Compartir PDF'}
            </span>
          </button>
        </div>
      )}

      {/* Modal para compartir PDF (todos los dispositivos) */}
      <Modal
        isOpen={showPdfShareModal}
        onClose={() => {
          setShowPdfShareModal(false);
          setPdfUrl('');
          setUrlCopied(false);
          setIsGeneratingLink(false);
          setLinkProgress(0);
        }}
        onConfirm={handleCopyPdfUrl}
        confirmText="Copiar URL"
        cancelText="Cerrar"
      >
        {isGeneratingLink ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '20px',
            color: '#64748b',
            fontFamily: "'Montserrat', sans-serif",
            fontSize: '14px'
          }}>
            <div className="upload-progress-text" style={{ marginBottom: '16px' }}>
              Generando enlace...
            </div>
            <div className="upload-progress-bar-container">
              <div 
                className="upload-progress-bar" 
                style={{ width: `${linkProgress}%` }}
              ></div>
            </div>
            <div className="upload-progress-percentage">{Math.round(linkProgress)}%</div>
          </div>
        ) : (
          <>
            <p style={{ marginBottom: '12px' }}>
              Usa esta URL para compartir el PDF de tu perfil institucional:
            </p>
            <div className="share-url-container" style={{ 
              background: '#f9fafb', 
              padding: '12px', 
              borderRadius: '6px', 
              marginBottom: '12px',
              wordBreak: 'break-all',
              fontFamily: 'monospace',
              fontSize: '12px',
              color: '#1e293b'
            }}>
              {pdfUrl}
            </div>
            {urlCopied && (
              <div className="share-url-copied-message" style={{
                background: '#d1fae5',
                border: '1px solid #10b981',
                borderRadius: '6px',
                padding: '8px 12px',
                marginBottom: '12px',
                color: '#065f46',
                fontSize: '13px',
                fontFamily: "'Montserrat', sans-serif",
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{ fontSize: '16px' }}>✓</span>
                <span>URL copiada al portapapeles</span>
              </div>
            )}
            <p style={{ fontSize: '12px', marginTop: '12px' }}>
              Cualquier persona con este enlace podrá ver el PDF de tu perfil institucional.
            </p>
          </>
        )}
      </Modal>
    </div>
  );
};

export default ShareDropdown;
